import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
    List,
    Datagrid,
    DatagridConfigurable,
    TextField,
    DateField,
    useTranslate,
    Filter,
    FilterLiveSearch,
    TopToolbar,
    SelectColumnsButton,
    ExportButton,
    useRecordContext,
    useRedirect,
    NumberField,
    BooleanField,
    useListContext,
    FunctionField,
    useNotify,
    Button as RaButton
} from 'react-admin';
import { Chip, Card, CardContent, Stack, Box, Typography, FormControl, InputLabel, Select, MenuItem, Divider, Checkbox, ListItemText, Tooltip, TextField as MuiTextField, Button as MuiButton } from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { styled } from '@mui/material/styles';

export const BidderInfoField = () => {
    const record = useRecordContext();
    const redirect = useRedirect();
    
    if (!record) return null;
    
    const handleNameClick = (e) => {
        e.stopPropagation();
        if (record.bidder_id) {
            redirect('show', 'bidders', record.bidder_id);
        }
    };
    
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 160 }}>
            <Typography
                component="a"
                href={record.bidder_id ? `/bidders/${record.bidder_id}/show` : undefined}
                onClick={handleNameClick}
                sx={{
                    color: 'primary.main',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    fontWeight: 500,
                    fontSize: '0.875rem',
                    '&:hover': {
                        color: 'primary.dark',
                    }
                }}
            >
                {record.bidder_name || '-'}
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                NIP: {record.bidder_tax_number || '-'}
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                Telefon: {record.bidder_phone || '-'}
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                Ryzyko: {record.bidder_financial_risk_rating ? (
                    <Tooltip 
                        title={record.bidder_financial_risk_description || ''}
                        arrow
                        placement="top"
                    >
                        <Chip 
                            label={record.bidder_financial_risk_rating}
                            color={record.bidder_financial_risk_color || 'default'}
                            size="small"
                            sx={{ fontSize: '0.65rem', height: '18px', ml: 0.5, cursor: 'help' }}
                        />
                    </Tooltip>
                ) : (
                    <span style={{ fontStyle: 'italic' }}>Brak danych</span>
                )}
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                Wiarygodność: {record.bidder_company_credibility_rating != null && record.bidder_company_credibility_rating !== '' ? (
                    <Tooltip 
                        title={record.bidder_company_credibility_description || ''}
                        arrow
                        placement="top"
                    >
                        <Chip 
                            label={record.bidder_company_credibility_rating}
                            color={record.bidder_company_credibility_color || 'default'}
                            size="small"
                            sx={{ fontSize: '0.65rem', height: '18px', ml: 0.5, cursor: 'help' }}
                        />
                    </Tooltip>
                ) : (
                    <span style={{ fontStyle: 'italic' }}>Brak danych</span>
                )}
            </Typography>
        </Box>
    );
};

export const TenderInfoField = () => {
    const record = useRecordContext();
    const redirect = useRedirect();
    const translate = useTranslate();
    
    if (!record) return null;
    
    const handleTitleClick = (e) => {
        e.stopPropagation();
        if (record.tender_id) {
            redirect('show', 'tenders', record.tender_id);
        }
    };
    
    const renderStatusChip = (value, label) => {
        if (value === null || value === undefined) {
            return <Chip label="-" color="default" size="small" sx={{ fontSize: '0.7rem', height: '20px' }} />;
        }
        if (Number(value) === 0) {
            return <Chip label={translate('ra.boolean.false', 'Nie')} color="default" size="small" sx={{ fontSize: '0.7rem', height: '20px' }} />;
        }
        if (Number(value) > 0) {
            return <Chip label={translate('ra.boolean.true', 'Tak')} color="success" size="small" sx={{ fontSize: '0.7rem', height: '20px' }} />;
        }
        return <Chip label="-" color="default" size="small" sx={{ fontSize: '0.7rem', height: '20px' }} />;
    };
    
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 200 }}>
            <Typography
                component="a"
                href={record.tender_id ? `/tenders/${record.tender_id}/show` : undefined}
                onClick={handleTitleClick}
                sx={{
                    color: 'primary.main',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    fontWeight: 500,
                    fontSize: '0.875rem',
                    lineHeight: 1.2,
                    '&:hover': {
                        color: 'primary.dark',
                    }
                }}
            >
                {record.tender_title || '-'}
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                {record.tender_notice_number || '-'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mt: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', fontWeight: 500 }}>
                        {translate('resources.tenders.fields.bid_bond', 'Wadium')}:
                    </Typography>
                    {renderStatusChip(record.tender_bid_bond)}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', fontWeight: 500 }}>
                        {translate('resources.tenders.fields.nwu', 'NWU')}:
                    </Typography>
                    {renderStatusChip(record.tender_nwu)}
                </Box>
            </Box>
        </Box>
    );
};

export const FinancialRiskField = ({ source }) => {
    const record = useRecordContext();
    if (!record) return null;
    
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {record.bidder_financial_risk_rating ? (
                <Tooltip 
                    title={record.bidder_financial_risk_description || ''}
                    arrow
                    placement="top"
                >
                    <Chip 
                        label={record.bidder_financial_risk_rating}
                        color={record.bidder_financial_risk_color || 'default'}
                        size="small"
                        sx={{ fontSize: '0.75rem', height: '24px', cursor: 'help' }}
                    />
                </Tooltip>
            ) : (
                <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', fontStyle: 'italic' }}>
                    Brak danych
                </Typography>
            )}
        </Box>
    );
};

export const MarketingConsentsField = () => {
    const record = useRecordContext();
    const translate = useTranslate();
    
    if (!record) return null;
    
    const renderConsentChip = (value) => {
        if (value === null || value === undefined) {
            return <Chip label="-" color="default" size="small" sx={{ fontSize: '0.7rem', height: '20px' }} />;
        }
        if (value === true || value === 1) {
            return <Chip label={translate('ra.boolean.true', 'Tak')} color="success" size="small" sx={{ fontSize: '0.7rem', height: '20px' }} />;
        }
        return <Chip label={translate('ra.boolean.false', 'Nie')} color="default" size="small" sx={{ fontSize: '0.7rem', height: '20px' }} />;
    };
    
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 180 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', fontWeight: 500 }}>
                    Zgoda na email:
                </Typography>
                {renderConsentChip(record.consent_email)}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', fontWeight: 500 }}>
                    Zgoda na telefon:
                </Typography>
                {renderConsentChip(record.consent_phone)}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', fontWeight: 500 }}>
                    Zgoda na grupę firm:
                </Typography>
                {renderConsentChip(record.consent_group_company)}
            </Box>
        </Box>
    );
};

const LeadStatusField = ({ source }) => {
    const record = useRecordContext();
    if (!record) return null;
    
    const isLead = record.is_lead || record.status === 'LEAD';
    
    return (
        <Chip 
            label={isLead ? 'Lead' : record.status || '-'} 
            color={isLead ? 'success' : 'default'} 
            size="small" 
        />
    );
};

export const OfferStatusField = ({ source }) => {
    const record = useRecordContext();
    const translate = useTranslate();
    if (!record) return null;

    const chips = [];

    // Winner status chip - only show if winner
    const winnerValue = record.is_winner;
    if (
        winnerValue === true ||
        winnerValue === 'true' ||
        winnerValue === 1 ||
        winnerValue === '1'
    ) {
        chips.push(
            <Chip key="winner" label="Winner" color="success" size="small" />
        );
    }

    // Bidder status chip
    const statusValue = record[source];
    if (statusValue && statusValue !== '') {
        let statusColor = 'default';
        let statusLabel = statusValue;

        switch (statusValue) {
            case 'LEAD':
                statusColor = 'info';
                statusLabel = 'Lead';
                break;
            case 'REJECTED':
                statusColor = 'error';
                statusLabel = 'Odrzucony';
                break;
            case 'PARTICIPANT':
                statusColor = 'warning';
                statusLabel = 'Uczestnik';
                break;
            default:
                statusColor = 'default';
                statusLabel = statusValue;
        }

        chips.push(
            <Chip key="status" label={statusLabel} color={statusColor} size="small" />
        );
    }

    if (chips.length === 0) return null;

    return (
        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>{chips}</div>
    );
};

export const WorkflowInfoField = () => {
    const record = useRecordContext();
    const translate = useTranslate();
    
    const getWorkflowStatus = () => {
        if (!record || !record.workflow_status) {
            return { color: 'default', label: 'Brak workflow' };
        }
        
        const status = record.workflow_status;
        switch (status) {
            case 'failed':
                return { color: 'error', label: 'Nieudany' };
            case 'running':
                return { color: 'warning', label: 'W trakcie' };
            case 'done':
                return { color: 'success', label: 'Zakończony' };
            case 'pending':
                return { color: 'info', label: 'Oczekujący' };
            case 'waiting':
                return { color: 'warning', label: 'Oczekuje' };
            case 'archived':
                return { color: 'default', label: 'Zarchiwizowany' };
            default:
                return { color: 'default', label: status || '-' };
        }
    };
    
    const getErrorMessages = () => {
        if (!record || !record.workflow_error_message) return null;
        
        if (Array.isArray(record.workflow_error_message)) {
            return record.workflow_error_message;
        }
        
        try {
            const parsed = JSON.parse(record.workflow_error_message);
            if (Array.isArray(parsed)) {
                return parsed;
            }
            return [String(parsed)];
        } catch (e) {
            return [String(record.workflow_error_message)];
        }
    };
    
    const statusInfo = getWorkflowStatus();
    const errorMessages = getErrorMessages();
    const currentBlock = record?.workflow_current_block;
    const statusChip = (
        <Chip label={statusInfo.label} color={statusInfo.color} size="small" />
    );
    
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: '200px', maxWidth: '280px' }}>
            {/* Status with Error Tooltip */}
            {errorMessages && errorMessages.length > 0 ? (
                <Tooltip
                    title={
                        <Box sx={{ p: 0.5 }}>
                            {errorMessages.map((error, idx) => (
                                <Typography
                                    key={idx}
                                    sx={{
                                        fontSize: '0.75rem',
                                        color: 'white',
                                        mb: idx < errorMessages.length - 1 ? 0.5 : 0,
                                        wordBreak: 'break-word',
                                        lineHeight: 1.4
                                    }}
                                >
                                    {error}
                                </Typography>
                            ))}
                        </Box>
                    }
                    arrow
                    placement="top"
                >
                    {statusChip}
                </Tooltip>
            ) : (
                statusChip
            )}
            
            {currentBlock && statusInfo.label !== 'Zakończony' && (
                <Box sx={{ mt: 0.5 }}>
                    <Typography variant="body2" sx={{ 
                        fontSize: '0.75rem',
                        color: 'text.secondary',
                        mb: 0.25
                    }}>
                        Krok:
                    </Typography>
                    <Typography variant="body2" sx={{ 
                        fontSize: '0.875rem',
                        color: 'text.primary',
                        wordBreak: 'break-word',
                        lineHeight: 1.3
                    }}>
                        {currentBlock}
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

export const StyledList = styled(List)({
  '& .MuiPaper-root': {
    padding: '12px 16px !important',
    border: '1px solid #e0e0e0',
    backgroundColor: '#fff',
  },
  '& .MuiToolbar-root': {
    minHeight: '40px !important',
    paddingLeft: 0,
    paddingRight: 0,
    marginBottom: '0px',
  }
});

const BidderOfferLeadFilter = (props) => {
    const translate = useTranslate();
    return (
        <Filter {...props}>
            <FilterLiveSearch source="bidder_name" label="Nazwa oferenta" />
            <FilterLiveSearch source="bidder_tax_number" label="NIP" />
            <FilterLiveSearch source="bidder_email" label="Email" />
            <FilterLiveSearch source="tender_notice_number" label="Numer ogłoszenia" />
            <FilterLiveSearch source="tender_title" label="Tytuł przetargu" />
            <FilterLiveSearch source="current_block" label="Aktualny proces" />
        </Filter>
    );
};

// Export CSV Button
const ExportCSVButton = () => {
  const { filterValues, sort } = useListContext();
  const [loading, setLoading] = useState(false);
  const notify = useNotify();
  const translate = useTranslate();
  const location = useLocation();

  const handleExport = async () => {
    setLoading(true);
    try {
      // Build query parameters to match current filters and sorting
      const params = new URLSearchParams();

      // Add filters
      Object.entries(filterValues || {}).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value);
        }
      });

      // Add sorting
      if (sort?.field) {
        params.append('_sort', sort.field);
        params.append('_order', sort.order?.toLowerCase() || 'desc');
      }

      // Determine the correct endpoint and filename based on current route
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
      let exportEndpoint;
      let filenamePrefix;
      
      if (location.pathname.includes('/bidders/marketing-consent')) {
        exportEndpoint = `${apiUrl}/bidders/marketing-consent/export/`;
        filenamePrefix = 'bidders_marketing_consent';
      } else {
        // Default to leads endpoint
        exportEndpoint = `${apiUrl}/bidders/leads/export/`;
        filenamePrefix = 'bidders_leads';
      }

      const response = await fetch(`${exportEndpoint}?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json',
        },
      });
      
      const result = await response.json();
      
      if (!response.ok) {
        const errorMessage = result.error || 'Export failed';
        notify(errorMessage, { type: 'error' });
        return;
      }
      
      const data = result.data;

      if (!data || data.length === 0) {
        notify('Brak danych do eksportu', { type: 'warning' });
        return;
      }
      
      // Display warning if export was limited
      if (result.warning) {
        notify(result.warning, { type: 'warning' });
      }
      
      // Helper function to properly escape CSV values
      // Using semicolon as separator for Polish Excel compatibility
      const escapeCsvValue = (value) => {
        if (value === null || value === undefined) return '';
        const stringValue = String(value);
        // Escape double quotes and wrap in quotes if contains semicolon, quote, or newline
        if (stringValue.includes(';') || stringValue.includes('"') || stringValue.includes('\n')) {
          return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
      };
      
      // Helper function to format numeric values with comma instead of dot for Excel
      const formatNumericValue = (value) => {
        if (value === null || value === undefined || value === '') return '';
        // Convert to string and replace dot with comma
        const stringValue = String(value);
        return stringValue.replace('.', ',');
      };
      
      // Flatten the data for CSV
      const flattenedData = data.map(record => ({
        id: record.id || '',
        bidder_name: escapeCsvValue(record.bidder_name),
        bidder_tax_number: record.bidder_tax_number || '',
        bidder_email: escapeCsvValue(record.bidder_email),
        bidder_phone: escapeCsvValue(record.bidder_phone),
        bidder_financial_risk_rating: record.bidder_financial_risk_rating || '',
        tender_title: escapeCsvValue(record.tender_title),
        tender_notice_number: record.tender_notice_number || '',
        tender_deadline: record.tender_deadline || '',
        tender_bid_bond: record.tender_bid_bond || '',
        tender_nwu: record.tender_nwu || '',
        offer_value: formatNumericValue(record.offer_value),
        offer_date: record.offer_date || '',
        status: record.status || '',
        workflow_status: record.workflow_status || '',
        workflow_current_block: escapeCsvValue(record.workflow_current_block),
        consent_email: record.consent_email ? 'Tak' : 'Nie',
        consent_phone: record.consent_phone ? 'Tak' : 'Nie',
        consent_group_company: record.consent_group_company ? 'Tak' : 'Nie',
      }));
      
      // Create CSV
      const headers = [
        'ID', 'Nazwa oferenta', 'NIP', 'Email', 'Telefon', 'Ryzyko finansowe',
        'Tytuł przetargu', 'Numer ogłoszenia', 'Termin składania ofert',
        'Wadium', 'NWU', 'Wartość oferty', 'Data oferty', 'Status',
        'Status procesu', 'Aktualny proces', 'Zgoda email', 'Zgoda telefon', 'Zgoda grupa firm'
      ];
      
      // Use semicolon as separator for Polish Excel (comma is decimal separator)
      const csvContent = [
        headers.join(';'),
        ...flattenedData.map(row => [
          row.id,
          row.bidder_name,
          row.bidder_tax_number,
          row.bidder_email,
          row.bidder_phone,
          row.bidder_financial_risk_rating,
          row.tender_title,
          row.tender_notice_number,
          row.tender_deadline,
          row.tender_bid_bond,
          row.tender_nwu,
          row.offer_value,
          row.offer_date,
          row.status,
          row.workflow_status,
          row.workflow_current_block,
          row.consent_email,
          row.consent_phone,
          row.consent_group_company
        ].join(';'))
      ].join('\n');
      
      // Download the CSV file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `${filenamePrefix}_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      notify(`Eksportowano ${data.length} rekordów`, { type: 'success' });
    } catch (error) {
      console.error('Export error:', error);
      notify('Błąd podczas eksportu', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <RaButton 
      label={loading ? 'Eksportowanie...' : "Export CSV"} 
      onClick={handleExport}
      disabled={loading}
      startIcon={<FileDownloadIcon />}
      sx={{
        backgroundColor: '#E31837',
        color: '#ffffff',
        '&:hover': {
          backgroundColor: '#C41430',
        },
        '& .MuiSvgIcon-root': {
          color: '#ffffff',
        },
      }}
    />
  );
};

export const ListActions = () => {
  return (
    <TopToolbar>             
      <SelectColumnsButton />      
      <ExportCSVButton />
    </TopToolbar>
  );
};

export const BidderOfferLeadFilterSidebar = () => {
  const translate = useTranslate();
  const location = useLocation();
  const { setFilters, filterValues } = useListContext();
  
  const [filterKey, setFilterKey] = useState(Date.now());
  const [from, setFrom] = useState(filterValues.tender_deadline_gte || '');
  const [to, setTo] = useState(filterValues.tender_deadline_lte || '');
  const [offerDateFrom, setOfferDateFrom] = useState(filterValues.offer_date_gte || '');
  const [offerDateTo, setOfferDateTo] = useState(filterValues.offer_date_lte || '');
  const [nwu, setNwu] = useState(filterValues.is_nwu || '');
  const [wadium, setWadium] = useState(filterValues.is_wadium || '');
  const [workflowStatus, setWorkflowStatus] = useState(filterValues.workflow_status || '');
  const [consentEmail, setConsentEmail] = useState(filterValues.consent_email === true || filterValues.consent_email === 'true' ? 'true' : (filterValues.consent_email === false || filterValues.consent_email === 'false' ? 'false' : ''));
  const [consentPhone, setConsentPhone] = useState(filterValues.consent_phone === true || filterValues.consent_phone === 'true' ? 'true' : (filterValues.consent_phone === false || filterValues.consent_phone === 'false' ? 'false' : ''));
  const [consentGroupCompany, setConsentGroupCompany] = useState(filterValues.consent_group_company === true || filterValues.consent_group_company === 'true' ? 'true' : (filterValues.consent_group_company === false || filterValues.consent_group_company === 'false' ? 'false' : ''));
  const [financialRisk, setFinancialRisk] = useState(() => {
    if (filterValues.financial_risk_rating) {
      return filterValues.financial_risk_rating.split(',').map(v => v.trim()).filter(v => v);
    }
    return [];
  });
  const [companyCredibility, setCompanyCredibility] = useState(() => {
    if (filterValues.company_credibility_rating) {
      return filterValues.company_credibility_rating.split(',').map(v => v.trim()).filter(v => v);
    }
    return [];
  });

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const urlFilters = {};
    let hasUrlFilters = false;

    const offerDateGte = searchParams.get('offer_date_gte');
    if (offerDateGte) {
      urlFilters.offer_date_gte = offerDateGte;
      hasUrlFilters = true;
    }

    const offerDateLte = searchParams.get('offer_date_lte');
    if (offerDateLte) {
      urlFilters.offer_date_lte = offerDateLte;
      hasUrlFilters = true;
    }

    const isNwu = searchParams.get('is_nwu');
    if (isNwu) {
      urlFilters.is_nwu = isNwu;
      hasUrlFilters = true;
    }

    const isWadium = searchParams.get('is_wadium');
    if (isWadium) {
      urlFilters.is_wadium = isWadium;
      hasUrlFilters = true;
    }

    if (hasUrlFilters) {
      const currentFilters = { ...filterValues };
      Object.keys(urlFilters).forEach(key => {
        currentFilters[key] = urlFilters[key];
      });
      Object.keys(currentFilters).forEach(key => {
        if (!urlFilters.hasOwnProperty(key) && 
            (key === 'offer_date_gte' || key === 'offer_date_lte' || key === 'is_nwu' || key === 'is_wadium')) {
          delete currentFilters[key];
        }
      });
      setFilters(currentFilters, {});
    }
  }, [location.search, setFilters]);

  useEffect(() => {
    if (filterValues.is_nwu !== undefined && filterValues.is_nwu !== null) {
      setNwu(filterValues.is_nwu === true || filterValues.is_nwu === 'true' ? 'true' : '');
    } else {
      setNwu('');
    }
  }, [filterValues.is_nwu]);

  useEffect(() => {
    if (filterValues.is_wadium !== undefined && filterValues.is_wadium !== null) {
      setWadium(filterValues.is_wadium === true || filterValues.is_wadium === 'true' ? 'true' : '');
    } else {
      setWadium('');
    }
  }, [filterValues.is_wadium]);

  useEffect(() => {
    if (filterValues.financial_risk_rating) {
      const values = filterValues.financial_risk_rating.split(',').map(v => v.trim()).filter(v => v);
      setFinancialRisk(values);
    } else {
      setFinancialRisk([]);
    }
  }, [filterValues.financial_risk_rating]);

  useEffect(() => {
    if (filterValues.company_credibility_rating) {
      const values = filterValues.company_credibility_rating.split(',').map(v => v.trim()).filter(v => v);
      setCompanyCredibility(values);
    } else {
      setCompanyCredibility([]);
    }
  }, [filterValues.company_credibility_rating]);

  useEffect(() => {
    if (filterValues.workflow_status) {
      setWorkflowStatus(filterValues.workflow_status);
    } else {
      setWorkflowStatus('');
    }
  }, [filterValues.workflow_status]);

  useEffect(() => {
    if (filterValues.tender_deadline_gte) {
      try {
        const date = new Date(filterValues.tender_deadline_gte);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        setFrom(`${year}-${month}-${day}`);
      } catch (e) {
        setFrom('');
      }
    } else {
      setFrom('');
    }
    
    if (filterValues.tender_deadline_lte) {
      try {
        const date = new Date(filterValues.tender_deadline_lte);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        setTo(`${year}-${month}-${day}`);
      } catch (e) {
        setTo('');
      }
    } else {
      setTo('');
    }
  }, [filterValues.tender_deadline_gte, filterValues.tender_deadline_lte]);

  useEffect(() => {
    if (filterValues.offer_date_gte) {
      try {
        const date = new Date(filterValues.offer_date_gte);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        setOfferDateFrom(`${year}-${month}-${day}`);
      } catch (e) {
        setOfferDateFrom('');
      }
    } else {
      setOfferDateFrom('');
    }
    
    if (filterValues.offer_date_lte) {
      try {
        const date = new Date(filterValues.offer_date_lte);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        setOfferDateTo(`${year}-${month}-${day}`);
      } catch (e) {
        setOfferDateTo('');
      }
    } else {
      setOfferDateTo('');
    }
  }, [filterValues.offer_date_gte, filterValues.offer_date_lte]);

  useEffect(() => {
    const hasAnyFilter = 
      filterValues.bidder_name || 
      filterValues.bidder_tax_number || 
      filterValues.bidder_email ||
      filterValues.bidder_registration_number ||
      filterValues.bidder_krs ||
      filterValues.bidder_pkd ||
      filterValues.bidder_pkd_others ||
      filterValues.bidder_city ||
      filterValues.bidder_municipality ||
      filterValues.bidder_province ||
      filterValues.tender_notice_number ||
      filterValues.tender_title ||
      filterValues.current_block ||
      filterValues.is_nwu ||
      filterValues.is_wadium ||
      filterValues.consent_email !== undefined ||
      filterValues.consent_phone !== undefined ||
      filterValues.consent_group_company !== undefined ||
      filterValues.is_current_customer !== undefined ||
      filterValues.financial_risk_rating ||
      filterValues.company_credibility_rating ||
      filterValues.tender_deadline_gte ||
      filterValues.tender_deadline_lte ||
      filterValues.offer_date_gte ||
      filterValues.offer_date_lte;
    
    if (!hasAnyFilter && Object.keys(filterValues).length === 0) {
      setFilterKey(Date.now());
    }
  }, [filterValues]);
  const getPolfundCustomerValue = () => {
    if (filterValues.is_current_customer === undefined || filterValues.is_current_customer === null) {
      return '';
    }
    return filterValues.is_current_customer === true || filterValues.is_current_customer === 'true' ? 'true' : 'false';
  };

  const handleNwuChange = (e) => {
    const value = e.target.value;
    setNwu(value);
    
    const newFilters = { ...filterValues };
    if (value === '') {
      delete newFilters.is_nwu;
    } else {
      newFilters.is_nwu = value;
    }
    setFilters(newFilters, {});
  };

  const handleWadiumChange = (e) => {
    const value = e.target.value;
    setWadium(value);
    
    const newFilters = { ...filterValues };
    if (value === '') {
      delete newFilters.is_wadium;
    } else {
      newFilters.is_wadium = value;
    }
    setFilters(newFilters, {});
  };

  const handlePolfundCustomerChange = (event) => {
    const value = event.target.value;
    const newFilters = { ...filterValues };
    
    if (value === '' || value === null || value === undefined) {
      delete newFilters.is_current_customer;
    } else {
      newFilters.is_current_customer = value === 'true';
    }
    
    setFilters(newFilters, {});
  };

  const handleFinancialRiskChange = (event) => {
    const value = event.target.value;
    const selectedValues = typeof value === 'string' ? value.split(',') : value;
    setFinancialRisk(selectedValues);
    
    const newFilters = { ...filterValues };
    if (!selectedValues || selectedValues.length === 0) {
      delete newFilters.financial_risk_rating;
    } else {
      newFilters.financial_risk_rating = selectedValues.join(',');
    }
    setFilters(newFilters, {});
  };

  const handleCompanyCredibilityChange = (event) => {
    const value = event.target.value;
    const selectedValues = typeof value === 'string' ? value.split(',') : value;
    setCompanyCredibility(selectedValues);
    
    const newFilters = { ...filterValues };
    if (!selectedValues || selectedValues.length === 0) {
      delete newFilters.company_credibility_rating;
    } else {
      newFilters.company_credibility_rating = selectedValues.join(',');
    }
    setFilters(newFilters, {});
  };

  const handleWorkflowStatusChange = (e) => {
    const value = e.target.value;
    setWorkflowStatus(value);
    
    const newFilters = { ...filterValues };
    if (value === '') {
      delete newFilters.workflow_status;
    } else {
      newFilters.workflow_status = value;
    }
    setFilters(newFilters, {});
  };

  const handleConsentEmailChange = (e) => {
    const value = e.target.value;
    setConsentEmail(value);
    
    const newFilters = { ...filterValues };
    if (value === '') {
      delete newFilters.consent_email;
    } else {
      newFilters.consent_email = value === 'true';
    }
    setFilters(newFilters, {});
  };

  const handleConsentPhoneChange = (e) => {
    const value = e.target.value;
    setConsentPhone(value);
    
    const newFilters = { ...filterValues };
    if (value === '') {
      delete newFilters.consent_phone;
    } else {
      newFilters.consent_phone = value === 'true';
    }
    setFilters(newFilters, {});
  };

  const handleConsentGroupCompanyChange = (e) => {
    const value = e.target.value;
    setConsentGroupCompany(value);
    
    const newFilters = { ...filterValues };
    if (value === '') {
      delete newFilters.consent_group_company;
    } else {
      newFilters.consent_group_company = value === 'true';
    }
    setFilters(newFilters, {});
  };

  const handleDateChange = (type, value) => {
    const newFilters = { ...filterValues };
    
    if (type === 'from') {
      setFrom(value);
      if (value === '') {
        delete newFilters.tender_deadline_gte;
      } else {
        const [year, month, day] = value.split('-').map(Number);
        const date = new Date(year, month - 1, day, 0, 0, 0, 0);
        newFilters.tender_deadline_gte = date.toISOString();
      }
    }
    
    if (type === 'to') {
      setTo(value);
      if (value === '') {
        delete newFilters.tender_deadline_lte;
      } else {
        const [year, month, day] = value.split('-').map(Number);
        const date = new Date(year, month - 1, day, 23, 59, 59, 999);
        newFilters.tender_deadline_lte = date.toISOString();
      }
    }
    
    setFilters(newFilters, {});
  };

  const handleOfferDateChange = (type, value) => {
    const newFilters = { ...filterValues };
    
    if (type === 'from') {
      setOfferDateFrom(value);
      if (value === '') {
        delete newFilters.offer_date_gte;
      } else {
        const [year, month, day] = value.split('-').map(Number);
        const date = new Date(year, month - 1, day, 0, 0, 0, 0);
        newFilters.offer_date_gte = date.toISOString();
      }
    }
    
    if (type === 'to') {
      setOfferDateTo(value);
      if (value === '') {
        delete newFilters.offer_date_lte;
      } else {
        const [year, month, day] = value.split('-').map(Number);
        const date = new Date(year, month - 1, day, 23, 59, 59, 999);
        newFilters.offer_date_lte = date.toISOString();
      }
    }
    
    setFilters(newFilters, {});
  };

  const handleResetFilters = () => {
    setFrom('');
    setTo('');
    setOfferDateFrom('');
    setOfferDateTo('');
    setNwu('');
    setWadium('');
    setConsentEmail('');
    setConsentPhone('');
    setConsentGroupCompany('');
    setFinancialRisk([]);
    setCompanyCredibility([]);
    
    setFilterKey(Date.now());
    
    requestAnimationFrame(() => {
      const allFilterFields = {
        bidder_name: '',
        bidder_tax_number: '',
        bidder_email: '',
        bidder_registration_number: '',
        bidder_krs: '',
        bidder_pkd: '',
        bidder_pkd_others: '',
        bidder_city: '',
        bidder_municipality: '',
        bidder_province: '',
        tender_notice_number: '',
        tender_title: '',
        current_block: '',
        is_nwu: '',
        is_wadium: '',
        consent_email: '',
        consent_phone: '',
        consent_group_company: '',
        is_current_customer: '',
        financial_risk_rating: '',
        company_credibility_rating: '',
        tender_deadline_gte: '',
        tender_deadline_lte: '',
        offer_date_gte: '',
        offer_date_lte: '',
      };
      
      setFilters(allFilterFields, {});
      
      requestAnimationFrame(() => {
        setFilters({}, {});
      });
    });
  };

  return (
    <Card sx={{ order: -1, mr: 2, mt: 9, minWidth: 250, maxWidth: 300, boxShadow: 0, borderRadius: 2 }}>
      <CardContent>
        <Stack spacing={1.5}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary', mt: 1, mb: 0.5 }}>
            Filtry po oferenci
          </Typography>
          
          <FilterLiveSearch key={`bidder_name-${filterKey}`} source="bidder_name" label={translate('resources.bidders.fields.name', 'Nazwa oferenta')} />
          <FilterLiveSearch key={`bidder_tax_number-${filterKey}`} source="bidder_tax_number" label={translate('resources.bidders.fields.tax_number', 'NIP')} />
          <FilterLiveSearch key={`bidder_email-${filterKey}`} source="bidder_email" label="Email" />
          <FilterLiveSearch key={`bidder_registration_number-${filterKey}`} source="bidder_registration_number" label={translate('resources.bidders.fields.registration_number', 'REGON')} />
          <FilterLiveSearch key={`bidder_krs-${filterKey}`} source="bidder_krs" label={translate('resources.bidders.fields.krs', 'KRS')} />
          <FilterLiveSearch key={`bidder_pkd-${filterKey}`} source="bidder_pkd" label={translate('resources.bidders.fields.pkd', 'PKD')} />
          <FilterLiveSearch key={`bidder_pkd_others-${filterKey}`} source="bidder_pkd_others" label="Pozostałe kody PKD" />
          <FilterLiveSearch key={`bidder_city-${filterKey}`} source="bidder_city" label={translate('resources.bidders.fields.city', 'Miasto')} />
          <FilterLiveSearch key={`bidder_municipality-${filterKey}`} source="bidder_municipality" label={translate('resources.bidders.fields.municipality', 'Gmina')} />
          <FilterLiveSearch key={`bidder_province-${filterKey}`} source="bidder_province" label={translate('resources.bidders.fields.province', 'Województwo')} />
          
          <FormControl fullWidth size="small" sx={{ mt: 0.5 }}>
            <InputLabel id="polfund-customer-filter-label" sx={{ fontSize: '0.875rem' }}>
              Czy klient Polfund
            </InputLabel>
            <Select
              labelId="polfund-customer-filter-label"
              value={getPolfundCustomerValue()}
              onChange={handlePolfundCustomerChange}
              label="Czy klient Polfund"
              sx={{ fontSize: '0.875rem' }}
            >
              <MenuItem value="">
                <em>Wszystkie</em>
              </MenuItem>
              <MenuItem value="true">TAK</MenuItem>
              <MenuItem value="false">NIE</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth size="small" sx={{ mt: 1 }}>
            <InputLabel id="financial-risk-filter-label" sx={{ fontSize: '0.875rem' }}>
              Skala ryzyka finansowego
            </InputLabel>
            <Select
              labelId="financial-risk-filter-label"
              multiple
              value={financialRisk}
              onChange={handleFinancialRiskChange}
              label="Skala ryzyka finansowego"
              sx={{ fontSize: '0.875rem' }}
              renderValue={(selected) => {
                if (selected.length === 0) return '';
                if (selected.length === 1) {
                  const labels = {
                    '5': '5',
                    '4+': '4+',
                    '4': '4',
                    '4-': '4-',
                    '3+': '3+',
                    '3': '3',
                    '3-': '3-',
                    '2': '2',
                    '?': '?',
                    'none': 'Brak danych'
                  };
                  return labels[selected[0]] || selected[0];
                }
                return `${selected.length} wybrane`;
              }}
            >
              <MenuItem value="5">
                <Checkbox checked={financialRisk.indexOf('5') > -1} />
                <ListItemText primary="5 - Znikome ryzyko finansowe" />
              </MenuItem>
              <MenuItem value="4+">
                <Checkbox checked={financialRisk.indexOf('4+') > -1} />
                <ListItemText primary="4+ - Minimalne ryzyko finansowe" />
              </MenuItem>
              <MenuItem value="4">
                <Checkbox checked={financialRisk.indexOf('4') > -1} />
                <ListItemText primary="4 - Bardzo niskie ryzyko finansowe" />
              </MenuItem>
              <MenuItem value="4-">
                <Checkbox checked={financialRisk.indexOf('4-') > -1} />
                <ListItemText primary="4- - Niskie ryzyko finansowe" />
              </MenuItem>
              <MenuItem value="3+">
                <Checkbox checked={financialRisk.indexOf('3+') > -1} />
                <ListItemText primary="3+ - Średnie ryzyko finansowe" />
              </MenuItem>
              <MenuItem value="3">
                <Checkbox checked={financialRisk.indexOf('3') > -1} />
                <ListItemText primary="3 - Wysokie ryzyko finansowe" />
              </MenuItem>
              <MenuItem value="3-">
                <Checkbox checked={financialRisk.indexOf('3-') > -1} />
                <ListItemText primary="3- - Bardzo wysokie ryzyko finansowe" />
              </MenuItem>
              <MenuItem value="2">
                <Checkbox checked={financialRisk.indexOf('2') > -1} />
                <ListItemText primary="2 - Krytycznie wysokie ryzyko finansowe" />
              </MenuItem>
              <MenuItem value="?">
                <Checkbox checked={financialRisk.indexOf('?') > -1} />
                <ListItemText primary="? - Brak danych do oceny finansowej" />
              </MenuItem>
              <MenuItem value="none">
                <Checkbox checked={financialRisk.indexOf('none') > -1} />
                <ListItemText primary="Brak danych" />
              </MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth size="small" sx={{ mt: 1 }}>
            <InputLabel id="company-credibility-filter-label" sx={{ fontSize: '0.875rem' }}>
              Skala wiarygodności
            </InputLabel>
            <Select
              labelId="company-credibility-filter-label"
              multiple
              value={companyCredibility}
              onChange={handleCompanyCredibilityChange}
              label="Skala wiarygodności"
              sx={{ fontSize: '0.875rem' }}
              renderValue={(selected) => {
                if (selected.length === 0) return '';
                if (selected.length === 1) {
                  const labels = {
                    '5': '5 - Współpraca wskazana',
                    '4': '4 - Zalecamy współpracę',
                    '3': '3 - Zalecamy ostrożność',
                    '2': '2 - Zalecamy szczególną ostrożność',
                    'none': 'Brak danych'
                  };
                  return labels[selected[0]] || selected[0];
                }
                return `${selected.length} wybrane`;
              }}
            >
              <MenuItem value="5">
                <Checkbox checked={companyCredibility.indexOf('5') > -1} />
                <ListItemText primary="5 - Współpraca wskazana" />
              </MenuItem>
              <MenuItem value="4">
                <Checkbox checked={companyCredibility.indexOf('4') > -1} />
                <ListItemText primary="4 - Zalecamy współpracę" />
              </MenuItem>
              <MenuItem value="3">
                <Checkbox checked={companyCredibility.indexOf('3') > -1} />
                <ListItemText primary="3 - Zalecamy ostrożność" />
              </MenuItem>
              <MenuItem value="2">
                <Checkbox checked={companyCredibility.indexOf('2') > -1} />
                <ListItemText primary="2 - Zalecamy szczególną ostrożność" />
              </MenuItem>
              <MenuItem value="none">
                <Checkbox checked={companyCredibility.indexOf('none') > -1} />
                <ListItemText primary="Brak danych" />
              </MenuItem>
            </Select>
          </FormControl>

          <Typography sx={{ fontWeight: 500, fontSize: 14, mt: 2 }}>
            Zgody marketingowe
          </Typography>

          <FormControl fullWidth size="small" sx={{ mt: 0.5 }}>
            <InputLabel id="consent-email-filter-label" sx={{ fontSize: '0.875rem' }}>
              Zgoda na email
            </InputLabel>
            <Select
              labelId="consent-email-filter-label"
              value={consentEmail}
              onChange={handleConsentEmailChange}
              label="Zgoda na email"
              sx={{ fontSize: '0.875rem' }}
            >
              <MenuItem value="">
                <em>Wszystkie</em>
              </MenuItem>
              <MenuItem value="true">TAK</MenuItem>
              <MenuItem value="false">NIE</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth size="small" sx={{ mt: 0.5 }}>
            <InputLabel id="consent-phone-filter-label" sx={{ fontSize: '0.875rem' }}>
              Zgoda na telefon
            </InputLabel>
            <Select
              labelId="consent-phone-filter-label"
              value={consentPhone}
              onChange={handleConsentPhoneChange}
              label="Zgoda na telefon"
              sx={{ fontSize: '0.875rem' }}
            >
              <MenuItem value="">
                <em>Wszystkie</em>
              </MenuItem>
              <MenuItem value="true">TAK</MenuItem>
              <MenuItem value="false">NIE</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth size="small" sx={{ mt: 0.5 }}>
            <InputLabel id="consent-group-company-filter-label" sx={{ fontSize: '0.875rem' }}>
              Zgoda na grupę firm
            </InputLabel>
            <Select
              labelId="consent-group-company-filter-label"
              value={consentGroupCompany}
              onChange={handleConsentGroupCompanyChange}
              label="Zgoda na grupę firm"
              sx={{ fontSize: '0.875rem' }}
            >
              <MenuItem value="">
                <em>Wszystkie</em>
              </MenuItem>
              <MenuItem value="true">TAK</MenuItem>
              <MenuItem value="false">NIE</MenuItem>
            </Select>
          </FormControl>

          <Divider sx={{ my: 1.5 }} />

          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
            Filtry po przetargu
          </Typography>
          
          <FilterLiveSearch key={`tender_notice_number-${filterKey}`} source="tender_notice_number" label="Numer ogłoszenia" />
          <FilterLiveSearch key={`tender_title-${filterKey}`} source="tender_title" label="Tytuł przetargu" />
          
          <FormControl fullWidth size="small" sx={{ mt: 1 }}>
            <InputLabel id="nwu-filter-label" sx={{ fontSize: '0.875rem' }}>
              {translate('resources.tenders.fields.is_nwu', 'Czy jest NWU')}
            </InputLabel>
            <Select
              labelId="nwu-filter-label"
              value={nwu}
              onChange={handleNwuChange}
              label={translate('resources.tenders.fields.is_nwu', 'Czy jest NWU')}
              sx={{ fontSize: '0.875rem' }}
            >
              <MenuItem value="">
                <em>Wszystkie</em>
              </MenuItem>
              <MenuItem value="true">{translate('ra.boolean.true', 'Tak')}</MenuItem>
              <MenuItem value="false">{translate('ra.boolean.false', 'Nie')}</MenuItem>
            </Select>
          </FormControl>
          
          <FormControl fullWidth size="small" sx={{ mt: 1 }}>
            <InputLabel id="wadium-filter-label" sx={{ fontSize: '0.875rem' }}>
              {translate('resources.tenders.fields.is_wadium', 'Czy jest Wadium')}
            </InputLabel>
            <Select
              labelId="wadium-filter-label"
              value={wadium}
              onChange={handleWadiumChange}
              label={translate('resources.tenders.fields.is_wadium', 'Czy jest Wadium')}
              sx={{ fontSize: '0.875rem' }}
            >
              <MenuItem value="">
                <em>Wszystkie</em>
              </MenuItem>
              <MenuItem value="true">{translate('ra.boolean.true', 'Tak')}</MenuItem>
              <MenuItem value="false">{translate('ra.boolean.false', 'Nie')}</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth size="small" sx={{ mt: 1 }}>
            <InputLabel id="workflow-status-filter-label" sx={{ fontSize: '0.875rem' }}>
              Status procesu
            </InputLabel>
            <Select
              labelId="workflow-status-filter-label"
              value={workflowStatus}
              onChange={handleWorkflowStatusChange}
              label="Status procesu"
              sx={{ fontSize: '0.875rem' }}
            >
              <MenuItem value="">
                <em>Wszystkie</em>
              </MenuItem>
              <MenuItem value="failed">Nieudany</MenuItem>
              <MenuItem value="running">W trakcie</MenuItem>
              <MenuItem value="done">Zakończony</MenuItem>
              <MenuItem value="pending">Oczekujący</MenuItem>
              <MenuItem value="waiting">Oczekuje</MenuItem>
              <MenuItem value="none">Brak workflow</MenuItem>
            </Select>
          </FormControl>
          
          <FilterLiveSearch key={`current_block-${filterKey}`} source="current_block" label="Aktualny proces" />
          
          <Typography sx={{ fontWeight: 500, fontSize: 14, mt: 1 }}>
            Termin składania ofert
          </Typography>
          <MuiTextField
            label="Od"
            type="date"
            value={from}
            onChange={e => handleDateChange('from', e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            margin="dense"
            size="small"
            variant="outlined"
          />
          <MuiTextField
            label="Do"
            type="date"
            value={to}
            onChange={e => handleDateChange('to', e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            margin="dense"
            size="small"
            variant="outlined"
          />
          
          <Typography sx={{ fontWeight: 500, fontSize: 14, mt: 1 }}>
            Data oferty
          </Typography>
          <MuiTextField
            label="Od"
            type="date"
            value={offerDateFrom}
            onChange={e => handleOfferDateChange('from', e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            margin="dense"
            size="small"
            variant="outlined"
          />
          <MuiTextField
            label="Do"
            type="date"
            value={offerDateTo}
            onChange={e => handleOfferDateChange('to', e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            margin="dense"
            size="small"
            variant="outlined"
          />

          <MuiButton 
            variant="outlined" 
            size="small" 
            onClick={handleResetFilters}
            sx={{ mt: 3, mb: 1 }}
            fullWidth
          >
            Wyczyść filtry
          </MuiButton>
        </Stack>
      </CardContent>
    </Card>
  );
};

export const BidderOfferLeadList = props => {
    const translate = useTranslate();
    return (
        <StyledList 
            {...props}
            actions={<ListActions />}
            perPage={25}
            sort={{ field: 'offer_date', order: 'DESC' }}
            aside={<BidderOfferLeadFilterSidebar />}
            title={translate('resources.bidders.leads', 'Oferenci Leady')}
        >
            <div style={{ width: '100%', overflowX: 'auto' }}>
                <DatagridConfigurable 
                    sx={{ 
                        minWidth: 'auto',
                        '& .MuiTableCell-root': {
                            padding: '8px 12px !important',
                            verticalAlign: 'top !important',
                            minHeight: '60px !important',
                            height: 'auto !important',
                            overflow: 'visible !important'
                        },
                        '& .MuiTableRow-root': {
                            minHeight: '60px !important',
                            height: 'auto !important'
                        },
                        '& .column-id': { minWidth: '60px', maxWidth: '60px' },
                        '& .column-bidder_info': { minWidth: '180px', maxWidth: '220px' },
                        '& .column-bidder_financial_risk_rating': { minWidth: '100px', maxWidth: '120px' },
                        '& .column-bidder_notes': { minWidth: '150px', maxWidth: '250px' },
                        '& .column-tender_info': { minWidth: '220px', maxWidth: '280px' },
                        '& .column-tender_deadline': { minWidth: '140px', maxWidth: '140px' },
                        '& .column-marketing_consents': { minWidth: '180px', maxWidth: '220px' },
                        '& .column-offer_value': { minWidth: '120px', maxWidth: '140px' },
                        '& .column-status': { minWidth: '100px', maxWidth: '120px' },
                        '& .column-offer_date': { minWidth: '140px', maxWidth: '140px' },
                        '& .column-workflow_info': { minWidth: '200px', maxWidth: '280px' }
                    }}
                    rowClick={false}
                    bulkActionButtons={false}
                    omit={[
                        'bidder_id',
                        'bidder_name',
                        'bidder_tax_number', 
                        'bidder_email',
                        'bidder_registration_number',
                        'bidder_krs',
                        'bidder_pkd',
                        'bidder_city',
                        'bidder_municipality',
                        'bidder_province',
                        'tender_id',
                        'tender_notice_number',
                        'tender_title',
                        'tender_value',
                        'notes',
                        'workflow_status',
                        'workflow_error_message',
                        'workflow_current_block',
                        'bidder_financial_risk_rating',
                        'consent_email',
                        'consent_phone',
                        'consent_group_company'
                    ]}
                >
                    <TextField source="id" label="ID" />
                    <BidderInfoField source="bidder_info" label="Oferent" />
                    <FunctionField 
                        source="bidder_financial_risk_rating" 
                        label="Ryzyko" 
                        sortBy="bidder_financial_risk_rating"
                        render={() => <FinancialRiskField />}
                    />
                    <TenderInfoField source="tender_info" label="Przetarg" />
                    <DateField source="tender_deadline" label="Termin składania ofert" showTime />
                    <FunctionField 
                        source="marketing_consents" 
                        label="Zgody marketingowe" 
                        render={() => <MarketingConsentsField />}
                    />
                    <NumberField source="offer_value" label="Wartość oferty" options={{ style: 'currency', currency: 'PLN' }} />
                    <OfferStatusField source="status" label="Status" />
                    <DateField source="offer_date" label="Data oferty" showTime />
                    <FunctionField 
                        source="workflow_info" 
                        label="Status procesu" 
                        sortBy="workflow_status"
                        render={() => <WorkflowInfoField />}
                    />
                    <FunctionField
                        source="bidder_notes"
                        label="Notatka"
                        render={(record) => (
                            <Box sx={{ 
                                maxWidth: '250px', 
                                wordBreak: 'break-word',
                                whiteSpace: 'pre-wrap',
                                fontSize: '0.875rem'
                            }}>
                                {record?.bidder_notes || '-'}
                            </Box>
                        )}
                    />
                </DatagridConfigurable>
            </div>
        </StyledList>
    );
};

export default {
    list: BidderOfferLeadList,
    hasList: true,
    hasShow: false,
    hasEdit: false,
    hasCreate: false,
};

